import { cmsClient } from '@/api';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
 


interface CaptchaSettings {
  siteKey: string;
  secretKey: string;
}



interface BrandingSettings {
  brandName: string;
  brandDomain: string;
  defaultLogo: string;
  iconLogo: string;
  variant1Logo: string;
  variant2Logo: string;
}


interface GeneralSettings {
  captcha: CaptchaSettings;
  branding: BrandingSettings;
}



interface SearchConsoleSettings {
  siteVerification: string;
}

interface SiteMapItems {
  url: string;
  lastModified?: Date;
  changeFrequency?: string;
  priority?: number;
}

interface SiteMap {
  items: SiteMapItems[];
  lastGenerated?: Date;
}

interface SEOSettings {
  searchConsole: SearchConsoleSettings;
  siteMap: SiteMap;
}

interface SiteSettings {
  general: GeneralSettings;
  seo: SEOSettings;
  [key: string]: any; // Diğer ayarlar için genişletilebilir
}


// SiteSettings Context
const SiteSettingsContext = createContext<{
  settings: SiteSettings;
  loading: boolean;
  error: string | null;
  refreshSettings: () => void;
  updateSetting: (key: string, value: any) => void;
  updateSettings: (newSettings: SiteSettings) => void;
} | null>(null);

// SiteSettings Provider Component
export const SiteSettingsProvider = ({ children }: {
  children: React.ReactNode;
}) => {
  const [settings, setSettings] = useState<SiteSettings>({
    general: {
      captcha: {
        siteKey: '',
        secretKey: ''
      },
      branding: {
        brandName: '',
        brandDomain: '',
        defaultLogo: '',
        iconLogo: '',
        variant1Logo: '',
        variant2Logo: ''
      }
    },
    seo: {
      searchConsole: {
        siteVerification: ''
      },
      siteMap: {
        items: [],
        lastGenerated: undefined
      }
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);



  // API fonksiyonu - kendi API endpoint'inizi buraya yazın
  const fetchSiteSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Buraya kendi API endpoint'inizi yazın
      const response = await cmsClient.v1.getSiteSettings();

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSettings(data);
    } catch (err: any) {
      setError(err.message);
      console.error('Site settings yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Ayarları yeniden yükleme fonksiyonu
  const refreshSettings = useCallback(() => {
    fetchSiteSettings();
  }, [fetchSiteSettings]);

  // Belirli bir ayarı güncelleme fonksiyonu
  const updateSetting = useCallback((key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Ayarları toplu güncelleme fonksiyonu
  const updateSettings = useCallback((newSettings: SiteSettings) => {
    setSettings(prev => ({
      ...prev,
      ...newSettings
    }));
  }, []);

  // Component mount olduğunda ayarları yükle
  useEffect(() => {
    fetchSiteSettings();
  }, [fetchSiteSettings]);

  // Context value
  const value = {
    settings,
    loading,
    error,
    refreshSettings,
    updateSetting,
    updateSettings
  }

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

// Hook - Site ayarlarını kullanmak için
export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);

  if (!context) {
    throw new Error('useSiteSettings hook SiteSettingsProvider içinde kullanılmalıdır');
  }

  return context;
};

// Hook - Belirli bir ayarı almak için
export const useSiteSettingSpec = (key: string, defaultValue = null) => {
  const { settings } = useSiteSettings();

  if (!settings) {
    return defaultValue;
  }

  return settings[key] || defaultValue;
};

// Hook - Nested ayarları almak için (örn: theme.colors.primary)
export const useNestedSiteSetting = (path: string, defaultValue = null) => {
  const { settings } = useSiteSettings();

  if (!settings) {
    return defaultValue;
  }

  // Path'i nokta ile ayır ve nested değeri al
  const keys = path.split('.');
  let value = settings;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }

  return value;
};

// Loading Component - Site ayarları yüklenirken gösterilecek
export const SiteSettingsLoader = ({ children, fallback = <div>Site ayarları yükleniyor...</div> }: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) => {
  const { loading, error } = useSiteSettings();

  if (loading) {
    return fallback;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        Site ayarları yüklenirken hata oluştu: {error}
      </div>
    );
  }

  return children;
};

// Örnek kullanım için App wrapper
export const AppWithSiteSettings = ({ children }: {
  children: React.ReactNode;
}) => {
  return (
    <SiteSettingsProvider>
      {children}
    </SiteSettingsProvider>
  );
};

/* 
KULLANIM ÖRNEKLERİ:

1. Ana App component'te provider'ı sarın:
```jsx
import { SiteSettingsProvider } from './providers/SiteSettings';

function App() {
  return (
    <SiteSettingsProvider>
      <Router>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </Router>
    </SiteSettingsProvider>
  );
}
```

2. Component'lerde ayarları kullanın:
```jsx
import { useSiteSettings, useSiteSetting } from './providers/SiteSettings';

function Header() {
  const { settings, loading } = useSiteSettings();
  const siteName = useSiteSetting('siteName', 'Varsayılan Site');
  const logoUrl = useSiteSetting('logoUrl');
  
  if (loading) return <div>Yükleniyor...</div>;
  
  return (
    <header>
      <h1>{siteName}</h1>
      {logoUrl && <img src={logoUrl} alt="Logo" />}
    </header>
  );
}
```

3. Nested ayarları kullanın:
```jsx
import { useNestedSiteSetting } from './providers/SiteSettings';

function ThemeComponent() {
  const primaryColor = useNestedSiteSetting('theme.colors.primary', '#007bff');
  const fontSize = useNestedSiteSetting('theme.typography.fontSize', '16px');
  
  return (
    <div style={{ color: primaryColor, fontSize }}>
      Themed content
    </div>
  );
}
```

4. Ayarları güncelleme:
```jsx
function SettingsPanel() {
  const { updateSetting, updateSettings } = useSiteSettings();
  
  const handleUpdateSiteName = () => {
    updateSetting('siteName', 'Yeni Site Adı');
  };
  
  const handleBulkUpdate = () => {
    updateSettings({
      siteName: 'Yeni Site',
      theme: { colors: { primary: '#ff0000' } }
    });
  };
  
  return (
    <div>
      <button onClick={handleUpdateSiteName}>Site Adını Güncelle</button>
      <button onClick={handleBulkUpdate}>Toplu Güncelle</button>
    </div>
  );
}
```

API ENDPOINT ÖRNEĞİ:
GET /api/site-settings
Response:
{
  "siteName": "Benim Sitem",
  "logoUrl": "/logo.png",
  "theme": {
    "colors": {
      "primary": "#007bff",
      "secondary": "#6c757d"
    },
    "typography": {
      "fontSize": "16px"
    }
  },
  "features": {
    "darkMode": true,
    "notifications": true
  }
}
*/